import { logger } from '../lib/utils';
import { useState, useCallback, useEffect, useRef } from 'react';
import { GRID_SIZE, GRID_SIZE_DESKTOP, GRID_SIZE_MOBILE, DIFFICULTIES, getPiecesCount } from '../lib/constants';
import { generatePuzzlePieces, isPieceCorrect, calculateScore, autoCorrectRotation } from '../lib/gameUtils';
import { useTimer } from './useTimer';
import { useImageManager } from './useImageManager';

/**
 * Main game state hook that composes smaller hooks for timer, images, and board management.
 * This is the primary interface for game components to interact with game state.
 * 
 * @param {string} deviceId - Device fingerprint ID
 * @param {number} gridSize - Grid dimension (4 for mobile, 6 for desktop)
 */
export function useGameState(deviceId = null, gridSize = GRID_SIZE_DESKTOP) {
  // Compose timer hook
  const timer = useTimer();
  
  // Compose image manager hook
  const imageManager = useImageManager(deviceId);
  
  // Current grid size (can change if device orientation changes)
  const [currentGridSize, setCurrentGridSize] = useState(gridSize);
  const totalPieces = getPiecesCount(currentGridSize);
  
  // Game-specific state
  const [difficulty, setDifficulty] = useState('normal');
  // Tray slots: fixed-size array with null for empty slots (pieces placed on board)
  const [traySlots, setTraySlots] = useState(Array(totalPieces).fill(null));
  const [board, setBoard] = useState(Array(currentGridSize).fill(null).map(() => Array(currentGridSize).fill(null)));
  const [moves, setMoves] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [incorrectPiece, setIncorrectPiece] = useState(null);
  
  // Track last completed puzzle for replay functionality
  const [lastCompletedPuzzle, setLastCompletedPuzzle] = useState(null);
  
  // First Score Feature: Track if this is the first play of the current image
  const [isFirstPlay, setIsFirstPlay] = useState(true);
  
  // Computed pieces array for backward compatibility (non-null tray slots)
  const pieces = traySlots.filter(p => p !== null);
  
  // Update grid size when prop changes (e.g., device orientation change)
  useEffect(() => {
    if (gridSize !== currentGridSize && moves === 0 && !timer.isRunning) {
      // Only change grid size if game hasn't started yet
      setCurrentGridSize(gridSize);
      setBoard(Array(gridSize).fill(null).map(() => Array(gridSize).fill(null)));
    }
  }, [gridSize, currentGridSize, moves, timer.isRunning]);
  
  // Check first play status when image changes
  useEffect(() => {
    const checkStatus = async () => {
      if (deviceId && imageManager.imageUrl && imageManager.checkFirstPlay) {
        const firstPlay = await imageManager.checkFirstPlay(imageManager.imageUrl);
        setIsFirstPlay(firstPlay);
      }
    };
    checkStatus();
    // Note: imageManager object changes every render, but we only care about imageUrl and checkFirstPlay
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, imageManager.imageUrl, imageManager.checkFirstPlay]);
  
  // Initialize game
  const initGame = useCallback(async (newImageUrl = imageManager.imageUrl, newDifficulty = difficulty, newGridSize = currentGridSize, checkFirst = true) => {
    const generatedPieces = generatePuzzlePieces(newImageUrl, newDifficulty, newGridSize);
    // Assign each piece a trayIndex based on its shuffled position
    const initialTray = generatedPieces.map((piece, index) => ({
      ...piece,
      trayIndex: index
    }));
    setTraySlots(initialTray);
    setBoard(Array(newGridSize).fill(null).map(() => Array(newGridSize).fill(null)));
    setMoves(0);
    setIsPaused(false);
    setIsComplete(false);
    setIncorrectPiece(null);
    setDifficulty(newDifficulty);
    setCurrentGridSize(newGridSize);
    imageManager.setImageUrl(newImageUrl);
    timer.resetTimer();
    
    // Check if this is a first play for the new image
    if (checkFirst && deviceId && imageManager.checkFirstPlay) {
      const firstPlay = await imageManager.checkFirstPlay(newImageUrl);
      setIsFirstPlay(firstPlay);
    }
  }, [imageManager, difficulty, currentGridSize, timer, deviceId]);
  
  // Pause/Resume game
  const togglePause = useCallback(() => {
    if (isComplete) return;
    
    if (isPaused) {
      setIsPaused(false);
      timer.resumeTimer();
    } else {
      setIsPaused(true);
      timer.pauseTimer();
    }
  }, [isPaused, isComplete, timer]);
  
  // Place piece on board (from tray or moving on board)
  const placePiece = useCallback((pieceId, row, col, fromTray = true, sourceRow = null, sourceCol = null) => {
    if (isPaused || isComplete) return { success: false };
    
    let piece;
    let trayIndex = null;
    
    if (fromTray) {
      // Find piece in tray slots
      trayIndex = traySlots.findIndex(p => p && p.id === pieceId);
      piece = traySlots[trayIndex];
    } else {
      piece = board[sourceRow]?.[sourceCol];
    }
    
    if (!piece) return { success: false };
    
    // Start timer on first move
    if (!timer.isRunning) {
      timer.startTimer();
    }
    
    // Increment moves
    setMoves(prev => prev + 1);
    
    // Check if position is correct
    let isCorrect = isPieceCorrect(piece, row, col, difficulty);
    let updatedPiece = { ...piece, currentRow: row, currentCol: col, isPlaced: true };
    
    // Auto-correct rotation in hard mode when position is correct
    if (DIFFICULTIES[difficulty].rotatesPieces && piece.correctRow === row && piece.correctCol === col) {
      updatedPiece = autoCorrectRotation(updatedPiece);
      isCorrect = true;
    }
    
    updatedPiece.isCorrect = isCorrect;
    
    // Update board
    const newBoard = board.map(r => [...r]);
    
    // Clear source position if moving on board
    if (!fromTray && sourceRow !== null && sourceCol !== null) {
      newBoard[sourceRow][sourceCol] = null;
    }
    
    newBoard[row][col] = updatedPiece;
    setBoard(newBoard);
    
    // Remove piece from tray (set slot to null) only if coming from tray
    if (fromTray && trayIndex !== null && trayIndex >= 0) {
      setTraySlots(prev => {
        const newSlots = [...prev];
        newSlots[trayIndex] = null;
        return newSlots;
      });
    }
    
    // Show incorrect feedback
    if (!isCorrect) {
      setIncorrectPiece({ row, col });
      setTimeout(() => setIncorrectPiece(null), 400);
    }
    
    // Check for completion
    const placedPieces = newBoard.flat().filter(p => p !== null);
    if (placedPieces.length === totalPieces) {
      const allCorrect = placedPieces.every(p => p.isCorrect);
      if (allCorrect) {
        setIsComplete(true);
        setLastCompletedPuzzle({ imageUrl: imageManager.imageUrl, difficulty, gridSize: currentGridSize });
        timer.stopTimer();
      }
    }
    
    return { success: true, isCorrect };
  }, [traySlots, board, isPaused, isComplete, timer, difficulty, imageManager.imageUrl, totalPieces, currentGridSize]);
  
  // Swap two pieces on the board
  const swapPieces = useCallback((pieceId, sourceRow, sourceCol, targetRow, targetCol, fromTray) => {
    if (isPaused || isComplete) return { success: false };
    
    // Start timer on first move
    if (!timer.isRunning) {
      timer.startTimer();
    }
    
    // Increment moves
    setMoves(prev => prev + 1);
    
    const newBoard = board.map(r => [...r]);
    const targetPiece = newBoard[targetRow][targetCol];
    
    let sourcePiece;
    let trayIndex = null;
    if (fromTray) {
      trayIndex = traySlots.findIndex(p => p && p.id === pieceId);
      sourcePiece = traySlots[trayIndex];
    } else {
      sourcePiece = newBoard[sourceRow]?.[sourceCol];
    }
    
    if (!sourcePiece || !targetPiece) return { success: false };
    
    // Update source piece to target position
    let updatedSourcePiece = { ...sourcePiece, currentRow: targetRow, currentCol: targetCol, isPlaced: true };
    let sourceCorrect = isPieceCorrect(sourcePiece, targetRow, targetCol, difficulty);
    if (DIFFICULTIES[difficulty].rotatesPieces && sourcePiece.correctRow === targetRow && sourcePiece.correctCol === targetCol) {
      updatedSourcePiece = autoCorrectRotation(updatedSourcePiece);
      sourceCorrect = true;
    }
    updatedSourcePiece.isCorrect = sourceCorrect;
    
    if (fromTray && trayIndex !== null && trayIndex >= 0) {
      // Source from tray, target goes back to its original tray position
      const resetTargetPiece = {
        ...targetPiece,
        currentRow: null,
        currentCol: null,
        isPlaced: false,
        isCorrect: false
      };
      
      newBoard[targetRow][targetCol] = updatedSourcePiece;
      setBoard(newBoard);
      
      // Put target piece back to its original tray slot, clear source slot
      setTraySlots(prev => {
        const newSlots = [...prev];
        newSlots[trayIndex] = null; // Clear source slot
        newSlots[targetPiece.trayIndex] = resetTargetPiece; // Return to original position
        return newSlots;
      });
    } else {
      // Both pieces are on board - swap them
      let updatedTargetPiece = { ...targetPiece, currentRow: sourceRow, currentCol: sourceCol, isPlaced: true };
      let targetCorrect = isPieceCorrect(targetPiece, sourceRow, sourceCol, difficulty);
      if (DIFFICULTIES[difficulty].rotatesPieces && targetPiece.correctRow === sourceRow && targetPiece.correctCol === sourceCol) {
        updatedTargetPiece = autoCorrectRotation(updatedTargetPiece);
        targetCorrect = true;
      }
      updatedTargetPiece.isCorrect = targetCorrect;
      
      newBoard[targetRow][targetCol] = updatedSourcePiece;
      newBoard[sourceRow][sourceCol] = updatedTargetPiece;
      setBoard(newBoard);
    }
    
    // Show incorrect feedback for target position
    if (!sourceCorrect) {
      setIncorrectPiece({ row: targetRow, col: targetCol });
      setTimeout(() => setIncorrectPiece(null), 400);
    }
    
    // Check for completion
    const placedPieces = newBoard.flat().filter(p => p !== null);
    if (placedPieces.length === totalPieces) {
      const allCorrect = placedPieces.every(p => p.isCorrect);
      if (allCorrect) {
        setIsComplete(true);
        setLastCompletedPuzzle({ imageUrl: imageManager.imageUrl, difficulty, gridSize: currentGridSize });
        timer.stopTimer();
      }
    }
    
    return { success: true };
  }, [traySlots, board, isPaused, isComplete, timer, difficulty, imageManager.imageUrl, totalPieces, currentGridSize]);
  
  // Remove piece from board back to tray
  const removePiece = useCallback((row, col) => {
    if (isPaused || isComplete) return;
    
    const piece = board[row][col];
    if (!piece) return;
    
    // Reset piece state
    const resetPiece = {
      ...piece,
      currentRow: null,
      currentCol: null,
      isPlaced: false,
      isCorrect: false
    };
    
    // Update board
    const newBoard = board.map(r => [...r]);
    newBoard[row][col] = null;
    setBoard(newBoard);
    
    // Add piece back to its original tray slot
    setTraySlots(prev => {
      const newSlots = [...prev];
      newSlots[piece.trayIndex] = resetPiece;
      return newSlots;
    });
  }, [board, isPaused, isComplete]);
  
  // Navigate to previous used image
  const previousImage = useCallback(() => {
    // Store current image before calling previousImage
    const currentImage = imageManager.imageUrl;
    const selectedImage = imageManager.previousImage();
    // Only init game if we got a different image
    if (selectedImage && selectedImage !== currentImage) {
      initGame(selectedImage, difficulty);
    }
  }, [imageManager, difficulty, initGame]);
  
  // Get random unused image
  const randomImage = useCallback(async () => {
    // Store current image before calling getRandomImage
    const currentImage = imageManager.imageUrl;
    const selectedImage = await imageManager.getRandomImage();
    if (selectedImage && selectedImage !== currentImage) {
      initGame(selectedImage, difficulty);
    }
  }, [imageManager, difficulty, initGame]);
  
  // Play with specific image
  const playWithImage = useCallback((url) => {
    imageManager.selectImage(url);
    initGame(url, difficulty);
  }, [imageManager, difficulty, initGame]);
  
  // Change difficulty
  const changeDifficulty = useCallback((newDifficulty) => {
    setDifficulty(newDifficulty);
    initGame(imageManager.imageUrl, newDifficulty, currentGridSize);
  }, [imageManager.imageUrl, initGame, currentGridSize]);
  
  // Calculate final score
  const getScore = useCallback(() => {
    return calculateScore(timer.time, moves, difficulty, currentGridSize);
  }, [timer.time, moves, difficulty, currentGridSize]);
  
  // Auto-solve puzzle with random high score (debug feature)
  const autoSolve = useCallback(() => {
    if (!imageManager.imageUrl) return;
    
    // Generate random realistic stats based on grid size
    const randomTime = Math.floor(Math.random() * 90) + 60;
    const mistakes = Math.floor(Math.random() * 13) + 2;
    const randomMoves = totalPieces + mistakes;
    
    // Create solved board
    const solvedBoard = Array(currentGridSize).fill(null).map(() => Array(currentGridSize).fill(null));
    
    for (let row = 0; row < currentGridSize; row++) {
      for (let col = 0; col < currentGridSize; col++) {
        const id = row * currentGridSize + col;
        solvedBoard[row][col] = {
          id,
          correctRow: row,
          correctCol: col,
          currentRow: row,
          currentCol: col,
          rotation: 0,
          isPlaced: true,
          isCorrect: true,
          imageUrl: imageManager.imageUrl,
          trayIndex: id // Keep trayIndex for consistency
        };
      }
    }
    
    // Stop timer and update state
    timer.stopTimer();
    timer.setTime(randomTime);
    
    setBoard(solvedBoard);
    // Clear all tray slots
    setTraySlots(Array(totalPieces).fill(null));
    setMoves(randomMoves);
    setIsPaused(false);
    
    // Store completed puzzle info for replay
    setLastCompletedPuzzle({ imageUrl: imageManager.imageUrl, difficulty, gridSize: currentGridSize });
    
    // Allow React to re-render before showing completion
    setTimeout(() => {
      setIsComplete(true);
      logger.log(`Auto-solved! Time: ${randomTime}s, Moves: ${randomMoves} (${mistakes} mistakes), Grid: ${currentGridSize}x${currentGridSize}`);
    }, 150);
  }, [imageManager.imageUrl, difficulty, timer, totalPieces, currentGridSize]);
  
  // Auto-solve puzzle with a target score (for high-score feature)
  // Simply generates a score that is 5-20 points above the provided target
  const autoSolveWithTargetScore = useCallback((targetScore) => {
    if (!imageManager.imageUrl) return;
    
    // Scoring formula:
    // timeScore = 500 * e^(-time/120)
    // moveScore = 300 * (totalPieces / max(moves, totalPieces))
    // completionBonus = 200
    // finalScore = (timeScore + moveScore + completionBonus) * multiplier
    
    const multiplier = DIFFICULTIES[difficulty]?.multiplier || 1.0;
    const minMoves = totalPieces;
    
    // We need to find time and moves that produce the target score
    // Start with good values and iterate to find the right combination
    
    // Target base score (before multiplier)
    const targetBase = targetScore / multiplier;
    
    // We want: timeScore + moveScore + 200 = targetBase
    // So: timeScore + moveScore = targetBase - 200
    const neededCombined = targetBase - 200;
    
    // Try different combinations to find one that works
    let bestTime = 45;
    let bestMoves = minMoves + 3;
    let bestScore = 0;
    
    // Search for good time/moves combination
    for (let testTime = 20; testTime <= 120; testTime += 5) {
      for (let testMoves = minMoves; testMoves <= minMoves + 15; testMoves++) {
        const timeScore = 500 * Math.exp(-testTime / 120);
        const moveScore = 300 * (minMoves / Math.max(testMoves, minMoves));
        const testScore = Math.round((timeScore + moveScore + 200) * multiplier);
        
        // Find the combination closest to our target
        if (Math.abs(testScore - targetScore) < Math.abs(bestScore - targetScore)) {
          bestScore = testScore;
          bestTime = testTime;
          bestMoves = testMoves;
        }
        
        // If we found an exact or very close match, use it
        if (Math.abs(testScore - targetScore) <= 2) {
          break;
        }
      }
      if (Math.abs(bestScore - targetScore) <= 2) {
        break;
      }
    }
    
    // Add small random variance to make it look natural
    const finalTime = bestTime + Math.floor(Math.random() * 5) - 2;
    const finalMoves = bestMoves + Math.floor(Math.random() * 3) - 1;
    
    // Clamp to reasonable values
    const calculatedTime = Math.max(15, Math.min(180, finalTime));
    const calculatedMoves = Math.max(minMoves, Math.min(minMoves * 2, finalMoves));
    
    // Create solved board
    const solvedBoard = Array(currentGridSize).fill(null).map(() => Array(currentGridSize).fill(null));
    
    for (let row = 0; row < currentGridSize; row++) {
      for (let col = 0; col < currentGridSize; col++) {
        const id = row * currentGridSize + col;
        solvedBoard[row][col] = {
          id,
          correctRow: row,
          correctCol: col,
          currentRow: row,
          currentCol: col,
          rotation: 0,
          isPlaced: true,
          isCorrect: true,
          imageUrl: imageManager.imageUrl,
          trayIndex: id
        };
      }
    }
    
    // Stop timer and update state
    timer.stopTimer();
    timer.setTime(calculatedTime);
    
    setBoard(solvedBoard);
    setTraySlots(Array(totalPieces).fill(null));
    setMoves(calculatedMoves);
    setIsPaused(false);
    
    // Store completed puzzle info for replay
    setLastCompletedPuzzle({ imageUrl: imageManager.imageUrl, difficulty, gridSize: currentGridSize });
    
    // Allow React to re-render before showing completion
    setTimeout(() => {
      setIsComplete(true);
      logger.log(`Auto-solved! Target: ${targetScore}, Best found: ${bestScore}, Time: ${calculatedTime}s, Moves: ${calculatedMoves}`);
    }, 150);
  }, [imageManager.imageUrl, difficulty, timer, totalPieces, currentGridSize]);
  
  // Replay the same puzzle
  const replayPuzzle = useCallback(() => {
    // First Score Feature: Mark as NOT first play when replaying
    setIsFirstPlay(false);
    
    if (lastCompletedPuzzle) {
      // Pass checkFirst=false to skip first-play check since we already know it's a replay
      initGame(lastCompletedPuzzle.imageUrl, lastCompletedPuzzle.difficulty, lastCompletedPuzzle.gridSize || currentGridSize, false);
    } else if (imageManager.imageUrl) {
      initGame(imageManager.imageUrl, difficulty, currentGridSize, false);
    }
  }, [lastCompletedPuzzle, imageManager.imageUrl, difficulty, initGame, currentGridSize]);
  
  // Add penalty move (e.g., for using piece overlay feature)
  const addPenaltyMove = useCallback(() => {
    if (isPaused || isComplete) return;
    
    // Start timer if not running (first interaction)
    if (!timer.isRunning) {
      timer.startTimer();
    }
    
    setMoves(prev => prev + 1);
  }, [isPaused, isComplete, timer]);
  
  // Reset incorrect pieces - returns incorrectly placed pieces to tray, shuffles tray
  // Does NOT reset timer, moves, or score
  const resetIncorrectPieces = useCallback(() => {
    if (isComplete) return;
    
    // Collect all pieces from board that are NOT correctly placed
    const incorrectPiecesFromBoard = [];
    const newBoard = board.map(r => [...r]);
    
    // Go through the board and collect incorrect pieces
    for (let row = 0; row < currentGridSize; row++) {
      for (let col = 0; col < currentGridSize; col++) {
        const piece = newBoard[row][col];
        if (piece && !piece.isCorrect) {
          // Reset piece properties for returning to tray
          incorrectPiecesFromBoard.push({
            ...piece,
            currentRow: null,
            currentCol: null,
            isPlaced: false,
            isCorrect: false
          });
          // Remove from board
          newBoard[row][col] = null;
        }
      }
    }
    
    // Update board with only correct pieces remaining
    setBoard(newBoard);
    
    // Get current tray pieces (non-null slots)
    const currentTrayPieces = traySlots.filter(p => p !== null);
    
    // Combine current tray pieces with returned incorrect pieces
    const allTrayPieces = [...currentTrayPieces, ...incorrectPiecesFromBoard];
    
    // Shuffle the combined pieces
    const shuffledPieces = [...allTrayPieces].sort(() => Math.random() - 0.5);
    
    // Create new tray slots with shuffled pieces
    const newTraySlots = Array(totalPieces).fill(null);
    shuffledPieces.forEach((piece, index) => {
      newTraySlots[index] = { ...piece, trayIndex: index };
    });
    
    setTraySlots(newTraySlots);
  }, [board, traySlots, currentGridSize, totalPieces, isComplete]);
  
  // Track if initial game setup has been done
  const initializedRef = useRef(false);
  
  // Initialize game when image becomes available (only once on mount)
  useEffect(() => {
    if (imageManager.imageUrl && !initializedRef.current) {
      initializedRef.current = true;
      initGame(imageManager.imageUrl, difficulty, currentGridSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageManager.imageUrl]);
  
  return {
    // State
    imageUrl: imageManager.imageUrl,
    difficulty,
    pieces,
    traySlots,
    board,
    moves,
    time: timer.time,
    isRunning: timer.isRunning,
    isPaused,
    isComplete,
    incorrectPiece,
    isLoadingImage: imageManager.isLoadingImage,
    usedImagesCount: imageManager.usedImagesCount,
    totalImagesCount: imageManager.totalImagesCount,
    hasNewImages: imageManager.hasNewImages,
    lastCompletedPuzzle,
    gridSize: currentGridSize,  // Expose current grid size
    canGoBack: imageManager.canGoBack,  // Can navigate to previous image
    viewingHistoryLength: imageManager.viewingHistoryLength,  // Number of viewed images
    isFirstPlay,  // First Score Feature: whether this is first play of current image
    
    // Actions
    initGame,
    placePiece,
    swapPieces,
    removePiece,
    togglePause,
    previousImage,
    randomImage,
    playWithImage,
    changeDifficulty,
    getScore,
    markImageAsUsed: imageManager.markImageAsUsed,
    autoSolve,
    autoSolveWithTargetScore,
    replayPuzzle,
    addPenaltyMove,
    resetIncorrectPieces,
    setIsFirstPlay  // Allow manual override (e.g., for replays)
  };
}
